<template>
  <div v-if="!isEditing">
    <p :class="$style.text">
      {{ childString }}
    </p>
  </div>

  <div v-else>
    <ul :class="$style.options">
      <li
        :class="$style.options__item"
        :key="index"
        v-for="(option, index) in options"
      >
        <label :class="$style.option">
          <DolphinRadioButton
            :modelValue="modelValue"
            :value="option.value"
            name="hasChild"
            @update:modelValue="emit('update:modelValue', option.value)"
          />
          <span :class="$style.option__text">
            {{ option.text }}
          </span>
        </label>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import DolphinRadioButton from '@/components/atoms/DolphinRadioButton.vue'

import * as Types from '@/types/schema.generated'

type Child = {
  gender: Types.Gender
  birthday: DateString
}

type FormChild = Child & {
  key: string
}

const props = withDefaults(
  defineProps<{
    modelValue?: Types.ChildrenStateAnswer
    isEditing?: boolean
  }>(),
  {
    modelValue: Types.ChildrenStateAnswer.Unanswered,
    isEditing: false,
  }
)

const emit = defineEmits<{
  (event: 'update:modelValue', value: Types.ChildrenStateAnswer): void
  (event: 'update:children', value: FormChild[]): void
}>()

const ChildrenStateAnswerToString: Record<Types.ChildrenStateAnswer, string> = {
  [Types.ChildrenStateAnswer.HasChildren]: 'いる',
  [Types.ChildrenStateAnswer.HasNoChildren]: 'いない',
  [Types.ChildrenStateAnswer.NoAnswer]: '回答しない',
  [Types.ChildrenStateAnswer.Unanswered]: '未回答', // 未回答はフォームとして表示しないが型のシンプルさのために、ここにも記述しておく
}

const childString = computed(
  () => ChildrenStateAnswerToString[props.modelValue]
)

const options: Array<{ value: Types.ChildrenStateAnswer; text: string }> = [
  {
    value: Types.ChildrenStateAnswer.HasChildren,
    text: ChildrenStateAnswerToString[Types.ChildrenStateAnswer.HasChildren],
  },
  {
    value: Types.ChildrenStateAnswer.HasNoChildren,
    text: ChildrenStateAnswerToString[Types.ChildrenStateAnswer.HasNoChildren],
  },
  {
    value: Types.ChildrenStateAnswer.NoAnswer,
    text: ChildrenStateAnswerToString[Types.ChildrenStateAnswer.NoAnswer],
  },
]
</script>

<style lang="scss" module>
.text {
  color: $color-text-and-object-high-emphasis;
  font-size: 16px;
  line-height: 28px;
}

.options {
  $gutter-horizontal: 40px;
  $gutter-vertical: 24px;

  display: flex;
  flex-wrap: wrap;
  margin-top: -$gutter-vertical;
  margin-left: -$gutter-horizontal;

  &__item {
    margin-top: $gutter-vertical;
    margin-left: $gutter-horizontal;
  }
}

.option {
  display: inline-flex;
  align-items: center;

  &__text {
    font-size: 14px;

    &:nth-child(n + 2) {
      margin-left: 4px;
    }
  }
}
</style>
